// Icon — SF Symbols-inspired icon library
// Hand-crafted SVG icons that closely match Apple's SF Symbols aesthetic
// Use these in place of emoji as primary UI icons

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  className?: string;
  weight?: 'regular' | 'bold';
  testId?: string;
}

export type IconName =
  | 'home' | 'chart' | 'wallet' | 'person' | 'gear' | 'search' | 'bell'
  | 'plus' | 'minus' | 'check' | 'xmark' | 'chevron-left' | 'chevron-right' | 'chevron-up' | 'chevron-down'
  | 'arrow-up' | 'arrow-down' | 'arrow-up-right' | 'arrow-down-right'
  | 'flame' | 'star' | 'heart' | 'lock' | 'unlock' | 'eye' | 'eye.slash'
  | 'doc' | 'doc.fill' | 'envelope' | 'envelope.fill' | 'phone' | 'message'
  | 'briefcase' | 'globe' | 'cart' | 'tag' | 'gift' | 'shield' | 'shield.fill'
  | 'flag' | 'flag.fill' | 'bolt' | 'bolt.fill' | 'cube' | 'cube.fill'
  | 'creditcard' | 'creditcard.fill' | 'banknote' | 'building.columns' | 'dollarsign'
  | 'gauge' | 'speedometer' | 'target' | 'binoculars' | 'scope'
  | 'tray' | 'tray.fill' | 'arrow.clockwise' | 'arrow.counterclockwise' | 'clock'
  | 'calendar' | 'calendar.fill' | 'map' | 'location' | 'pin' | 'bookmark' | 'bookmark.fill'
  | 'play' | 'play.fill' | 'pause' | 'pause.fill' | 'stop' | 'forward' | 'backward'
  | 'speaker' | 'speaker.wave' | 'wifi' | 'antenna' | 'battery.100' | 'battery.50'
  | 'moon' | 'sun.max' | 'cloud' | 'rain' | 'snow' | 'wind'
  | 'paintbrush' | 'scissors' | 'wand' | 'sparkles'
  | 'trash' | 'pencil' | 'paperplane' | 'link' | 'qrcode' | 'barcode'
  | 'list.bullet' | 'list.number' | 'square.grid' | 'rectangle.stack' | 'square.stack'
  | 'figure.run' | 'figure.boxing' | 'figure.strengthtraining.traditional' | 'figure.yoga'
  | 'leaf' | 'drop' | 'flame.fill' | 'bolt.horizontal' | 'bolt.horizontal.fill'
  | 'arrow.triangle.2.circlepath' | 'arrow.triangle.swap' | 'arrow.up.arrow.down'
  | 'person.2' | 'person.3' | 'person.crop.circle' | 'person.crop.circle.fill'
  | 'trophy' | 'trophy.fill' | 'medal' | 'rosette' | 'crown'
  | 'circle.dotted' | 'circle.dashed' | 'circle.fill' | 'circle'
  | 'exclamationmark.triangle' | 'exclamationmark.triangle.fill' | 'info.circle' | 'info.circle.fill'
  | 'questionmark.circle' | 'questionmark.circle.fill' | 'checkmark.circle' | 'checkmark.circle.fill'
  | 'xmark.circle' | 'xmark.circle.fill' | 'plus.circle' | 'plus.circle.fill'
  | 'minus.circle' | 'gear' | 'gearshape' | 'gearshape.fill'
  | 'slider.horizontal' | 'switch.2' | 'toggle'
  | 'play.rectangle' | 'video' | 'camera' | 'photo' | 'music.note'
  | 'mic' | 'mic.fill' | 'speaker.wave.2' | 'ear'
  | 'hand.thumbsup' | 'hand.thumbsup.fill' | 'hand.thumbsdown' | 'hand.point.up.left'
  | 'globe.americas' | 'globe.europe.africa' | 'globe.asia.australia'
  | 'arrow.up.circle' | 'arrow.up.circle.fill' | 'arrow.down.circle' | 'arrow.down.circle.fill'
  | 'arrow.right.circle' | 'arrow.right.circle.fill' | 'arrow.left.circle' | 'arrow.left.circle.fill';

const ICONS: Record<string, string> = {
  // System
  'home': 'M3 12l9-9 9 9v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z',
  'chart': 'M3 17l6-6 4 4 8-8M14 7h7v7',
  'wallet': 'M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5a2 2 0 0 1-2-2v0a2 2 0 0 1 2-2h14M16 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  'person': 'M16 11a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8',
  'gear': 'M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  'search': 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3',
  'bell': 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10 21a2 2 0 0 0 4 0',
  'plus': 'M12 5v14M5 12h14',
  'minus': 'M5 12h14',
  'check': 'M5 13l4 4L19 7',
  'xmark': 'M6 6l12 12M6 18L18 6',
  'chevron-left': 'M15 5l-7 7 7 7',
  'chevron-right': 'M9 5l7 7-7 7',
  'chevron-up': 'M5 15l7-7 7 7',
  'chevron-down': 'M5 9l7 7 7-7',
  'arrow-up': 'M12 19V5M5 12l7-7 7 7',
  'arrow-down': 'M12 5v14M5 12l7 7 7-7',
  'arrow-up-right': 'M7 17L17 7M9 7h8v8',
  'arrow-down-right': 'M7 7l10 10M17 9v8h-8',
  'flame': 'M12 2c2 4-1 6 1 9 1.5 2 4 2 4 5a5 5 0 0 1-10 0c0-2 1-3 1-5 0-3 2-5 4-9z',
  'star': 'M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z',
  'heart': 'M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.5-7 10-7 10z',
  'lock': 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14v10H5z',
  'unlock': 'M6 11V8a6 6 0 0 1 11-1.5M5 11h14v10H5z',
  'eye': 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  'eye.slash': 'M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6 0 10 7 10 7a18 18 0 0 1-3 4M6 6c-2.4 1.6-4 4-4 6 0 0 4 7 10 7 1.3 0 2.5-.3 3.6-.8M9.5 9.5a3 3 0 0 0 4.2 4.2',
  'doc': 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M8 13h8M8 17h6',
  'doc.fill': 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6z',
  'envelope': 'M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2zM3 7l9 6 9-6',
  'envelope.fill': 'M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2zM3 7l9 6 9-6',
  'phone': 'M5 4a2 2 0 0 1 2-2h2l3 6-3 2a12 12 0 0 0 7 7l2-3 6 3v2a2 2 0 0 1-2 2A19 19 0 0 1 5 4z',
  'message': 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-6l-5 4v-4H5a2 2 0 0 1-2-2z',
  'briefcase': 'M3 7h18v13H3zM9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2',
  'globe': 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20',
  'cart': 'M3 3h2l3 13h12l2-9H7M9 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM18 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  'tag': 'M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9z M7 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  'gift': 'M3 8h18v4H3zM5 12v9h14v-9M12 8v13M9 5a3 3 0 0 1 6 0v3H9z',
  'shield': 'M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z',
  'shield.fill': 'M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z',
  'flag': 'M5 21V4l9 3 5-3v12l-5 3-9-3z',
  'flag.fill': 'M5 21V4l9 3 5-3v12l-5 3-9-3z',
  'bolt': 'M13 2L4 14h6l-1 8 9-12h-6z',
  'bolt.fill': 'M13 2L4 14h6l-1 8 9-12h-6z',
  'cube': 'M12 2l9 5v10l-9 5-9-5V7zM3 7l9 5 9-5M12 12v10',
  'cube.fill': 'M12 2l9 5v10l-9 5-9-5V7zM3 7l9 5 9-5',
  'creditcard': 'M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2H2zM2 10h20v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z',
  'creditcard.fill': 'M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2H2zM2 10h20v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z',
  'banknote': 'M3 6h18v12H3zM3 10h18M7 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2zM17 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  'building.columns': 'M3 21h18M5 21V10M9 21V10M15 21V10M19 21V10M11 21V10M2 10l10-7 10 7z',
  'dollarsign': 'M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  'gauge': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 12l5-3',
  'speedometer': 'M5 17a8 8 0 0 1 14 0M12 13l3-3M9 13h.01M15 13h.01',
  'target': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  'binoculars': 'M3 7v10h6V7zM15 7v10h6V7zM9 12h6',
  'scope': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  'tray': 'M3 14h6l1 2h4l1-2h6M3 14v5a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-5M3 14V5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v9',
  'tray.fill': 'M3 14h6l1 2h4l1-2h6v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM3 14V5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v9z',
  'arrow.clockwise': 'M3 12a9 9 0 0 1 15-6.7L21 8M3 12l3 3 3-3M21 12a9 9 0 0 1-15 6.7L3 16M21 12l-3-3-3 3',
  'arrow.counterclockwise': 'M21 12a9 9 0 0 0-15-6.7L3 8M21 12l-3 3-3-3M3 12a9 9 0 0 0 15 6.7L21 16M3 12l3-3 3 3',
  'clock': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 7v5l3 2',
  'calendar': 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM3 10h18M8 2v4M16 2v4',
  'calendar.fill': 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM3 10h18M8 2v4M16 2v4',
  'map': 'M9 4l-6 2v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14',
  'location': 'M12 22s8-7 8-13a8 8 0 1 0-16 0c0 6 8 13 8 13z M12 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  'pin': 'M12 2v9l-4 4 4 9 4-9-4-4z',
  'bookmark': 'M6 3h12v18l-6-4-6 4z',
  'bookmark.fill': 'M6 3h12v18l-6-4-6 4z',
  'play': 'M6 4l14 8-14 8z',
  'play.fill': 'M6 4l14 8-14 8z',
  'pause': 'M7 4h4v16H7zM13 4h4v16h-4z',
  'pause.fill': 'M7 4h4v16H7zM13 4h4v16h-4z',
  'stop': 'M6 6h12v12H6z',
  'forward': 'M5 4l9 8-9 8zM14 4h2v16h-2z',
  'backward': 'M19 4l-9 8 9 8zM10 4H8v16h2z',
  'speaker': 'M3 9v6h4l5 4V5L7 9zM16 8a4 4 0 0 1 0 8',
  'speaker.wave': 'M3 9v6h4l5 4V5L7 9zM16 6a8 8 0 0 1 0 12M19 3a12 12 0 0 1 0 18',
  'wifi': 'M2 9a16 16 0 0 1 20 0M5 13a12 12 0 0 1 14 0M8 17a8 8 0 0 1 8 0M12 21h.01',
  'antenna': 'M5 12a7 7 0 0 1 14 0M2 8a11 11 0 0 1 20 0M8 16a3 3 0 0 1 6 0M12 20h.01',
  'battery.100': 'M3 7h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM21 10v4M5 10v4M7 10v4M9 10v4M11 10v4M13 10v4M15 10v4M17 10v4',
  'battery.50': 'M3 7h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM21 10v4M5 10v4M7 10v4M9 10v4M11 10v4',
  'moon': 'M21 13A9 9 0 1 1 11 3a7 7 0 0 0 10 10z',
  'sun.max': 'M12 4V2M12 22v-2M4 12H2M22 12h-2M6 6L4.5 4.5M19.5 19.5L18 18M6 18l-1.5 1.5M19.5 4.5L18 6 M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z',
  'cloud': 'M7 18a5 5 0 1 1 0-10 7 7 0 0 1 13 1 4 4 0 0 1 0 9z',
  'rain': 'M7 14a5 5 0 1 1 0-10 7 7 0 0 1 13 1 4 4 0 0 1 0 9zM9 18l-1 2M13 18l-1 2M17 18l-1 2',
  'snow': 'M12 2v20M2 12h20M5 5l14 14M19 5L5 19M12 4l-2 2 2 2 2-2zM12 16l-2 2 2 2 2-2z',
  'wind': 'M3 8h12a3 3 0 1 0-3-3M3 12h17a3 3 0 1 1-3 3M3 16h10',
  'paintbrush': 'M19 11l2-2-7-7-2 2zM14 6l-9 9-2 5 5-2 9-9z',
  'scissors': 'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM20 4L8.5 15.5M14.5 9.5L20 4M14.5 14.5L20 20',
  'wand': 'M15 4l3 3-12 12-3-3zM14 5l5 5M9 13l-3 3',
  'sparkles': 'M5 3v4M3 5h4M19 13v4M17 15h4M12 2l1.5 4 4 1.5-4 1.5L12 13l-1.5-4-4-1.5 4-1.5zM18 8l.7 2 2 .7-2 .7L18 13l-.7-2-2-.7 2-.7z',
  'trash': 'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M5 6l1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14M10 11v6M14 11v6',
  'pencil': 'M17 3l4 4-13 13-5 1 1-5z',
  'paperplane': 'M22 2L2 11l8 3 3 8zM10 14l5-5',
  'link': 'M10 14a5 5 0 0 0 7 0l3-3a5 5 0 1 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 1 0 7 7l1-1',
  'qrcode': 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h3v3h-3zM18 18h3v3h-3zM14 19h3M19 14v3',
  'barcode': 'M3 5v14M6 5v14M9 5v14M12 5v14M15 5v14M18 5v14M21 5v14',
  'list.bullet': 'M9 6h12M9 12h12M9 18h12M5 6h.01M5 12h.01M5 18h.01',
  'list.number': 'M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1',
  'square.grid': 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  'rectangle.stack': 'M3 4h18v4H3zM3 10h18v4H3zM3 16h18v4H3z',
  'square.stack': 'M3 3h18v18H3zM7 7h14v14H7z',
  'figure.run': 'M11 4a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM6 20l3-8 4 2 3 6M10 12l-4 4M14 14l4 4',
  'figure.boxing': 'M16 11l3-3-2-2-3 3-5-1-2 3 4 1 1 4 3 2 1 4',
  'figure.strengthtraining.traditional': 'M2 12h2M20 12h2M5 8h2v8H5zM17 8h2v8h-2zM8 10h8v4H8z',
  'figure.yoga': 'M12 4a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM6 12c2 0 4-2 6-2s4 2 6 2M12 10v6M8 22l4-6 4 6',
  'leaf': 'M21 3c-9 0-16 7-16 16 0 1 0 2 1 2 9 0 16-7 16-16 0-1 0-2-1-2zM5 19c2-5 7-9 13-11',
  'drop': 'M12 2s-7 8-7 13a7 7 0 1 0 14 0c0-5-7-13-7-13z',
  'flame.fill': 'M12 2c2 4-1 6 1 9 1.5 2 4 2 4 5a5 5 0 0 1-10 0c0-2 1-3 1-5 0-3 2-5 4-9z',
  'bolt.horizontal': 'M3 10h12l-4 4h6l-4 4M3 14h6',
  'bolt.horizontal.fill': 'M3 10h12l-4 4h6l-4 4M3 14h6',
  'arrow.triangle.2.circlepath': 'M19 8a8 8 0 0 1 0 8M5 16a8 8 0 0 1 0-8M17 5l2-1v4h-4M7 19l-2 1v-4h4',
  'arrow.triangle.swap': 'M7 16l-4-4 4-4M17 8l4 4-4 4M3 12h18M21 8l-4 4 4 4M3 12l4-4-4-4',
  'arrow.up.arrow.down': 'M7 4l-3 3 3 3M4 7h12M17 20l3-3-3-3M20 17H8',
  'person.2': 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M17 11a4 4 0 1 0 0-8M22 21c0-3.9-3.1-7-7-7',
  'person.3': 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM1 21c0-3.9 3.1-7 7-7s7 3.1 7 7M17 11a4 4 0 1 0 0-8M11 21c0-3.9 3.1-7 7-7s7 3.1 7 7M23 11a4 4 0 1 0 0-8M17 21c0-3.9 3.1-7 7-7',
  'person.crop.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 21c0-3.9 3.1-7 7-7s7 3.1 7 7',
  'person.crop.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  'trophy': 'M7 4h10v5a5 5 0 0 1-10 0zM5 4h2v3a3 3 0 0 1-3-3zM17 4h2a3 3 0 0 1-3 3zM9 14h6l-1 4h-4zM8 20h8v2H8z',
  'trophy.fill': 'M7 4h10v5a5 5 0 0 1-10 0zM5 4h2v3a3 3 0 0 1-3-3zM17 4h2a3 3 0 0 1-3 3zM9 14h6l-1 4h-4zM8 20h8v2H8z',
  'medal': 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM9 4l-2 4h10l-2-4zM9 16l-2 4h10l-2-4',
  'rosette': 'M12 2l3 5 5 1-4 4 1 5-5-3-5 3 1-5-4-4 5-1zM12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  'crown': 'M2 8l4 6 4-8 2 6 2-6 4 8 4-6-2 12H4z',
  'circle.dotted': 'M6 6h.01M10 6h.01M14 6h.01M18 6h.01M6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h.01M10 14h.01M14 14h.01M18 14h.01M6 18h.01M10 18h.01M14 18h.01M18 18h.01',
  'circle.dashed': 'M6 6h4M14 6h4M6 10h-2M18 10h2M6 14h-2M18 14h2M6 18h4M14 18h4',
  'circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
  'circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
  'exclamationmark.triangle': 'M12 4l10 17H2z M12 10v4M12 17v.01',
  'exclamationmark.triangle.fill': 'M12 4l10 17H2z M12 10v4M12 17v.01',
  'info.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 11v6M12 7v.01',
  'info.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 11v6M12 7v.01',
  'questionmark.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9 9a3 3 0 1 1 6 0c0 1-1 1.5-2 2s-1 1-1 2M12 17v.01',
  'questionmark.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9 9a3 3 0 1 1 6 0c0 1-1 1.5-2 2s-1 1-1 2M12 17v.01',
  'checkmark.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12l3 3 5-6',
  'checkmark.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12l3 3 5-6',
  'xmark.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9 9l6 6M15 9l-6 6',
  'xmark.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9 9l6 6M15 9l-6 6',
  'plus.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12h8M12 8v8',
  'plus.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12h8M12 8v8',
  'minus.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12h8',
  'gearshape': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9 9l-2-2 2-2M15 9l2-2-2-2M9 15l-2 2 2 2M15 15l2 2-2 2',
  'gearshape.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9 9l-2-2 2-2M15 9l2-2-2-2M9 15l-2 2 2 2M15 15l2 2-2 2',
  'slider.horizontal': 'M3 6h18M3 12h18M3 18h18M9 6h6M9 12h6M9 18h6',
  'switch.2': 'M8 4l-4 4 4 4M16 20l4-4-4-4M4 8h16M4 16h16',
  'toggle': 'M3 8h18a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2zM16 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  'play.rectangle': 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM10 8l6 4-6 4z',
  'video': 'M3 5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM22 6l-5 6 5 6z',
  'camera': 'M5 7h3l2-2h4l2 2h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  'photo': 'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM9 9a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM5 21l5-5 4 4 3-3 4 4',
  'music.note': 'M9 18V5l12-2v13M9 18a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM21 16a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  'mic': 'M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zM19 11a7 7 0 0 1-14 0M12 18v4M8 22h8',
  'mic.fill': 'M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3z',
  'speaker.wave.2': 'M3 9v6h4l5 4V5L7 9zM16 6a8 8 0 0 1 0 12',
  'ear': 'M6 18a4 4 0 0 0 4-4V8a2 2 0 1 1 4 0v6a2 2 0 0 0 4 0V8a6 6 0 1 0-12 0v6a4 4 0 0 0 0 4z',
  'hand.thumbsup': 'M7 10v11M15 5l-1 5h6a2 2 0 0 1 2 2l-2 7a2 2 0 0 1-2 2H7V10l4-7c1 0 2 1 2 2z',
  'hand.thumbsup.fill': 'M7 10v11M15 5l-1 5h6a2 2 0 0 1 2 2l-2 7a2 2 0 0 1-2 2H7V10l4-7c1 0 2 1 2 2z',
  'hand.thumbsdown': 'M17 14V3M9 19l1-5H4a2 2 0 0 1-2-2l2-7a2 2 0 0 1 2-2h10v11l-4 7c-1 0-2-1-2-2z',
  'hand.point.up.left': 'M9 6V3a2 2 0 1 0-4 0v9l-3 1c-1 0-2 1-2 2l4 7c1 1 2 1 3 1l8-2c1 0 2-1 2-2v-5l-8-8z',
  'globe.americas': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20M9 8l3 1 3-1M9 16l3-1 3 1',
  'globe.europe.africa': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20',
  'globe.asia.australia': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20M16 8l-2 1 1 2-2 1M8 14l2-1-1-2 2-1',
  'arrow.up.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16V8M8 12l4-4 4 4',
  'arrow.up.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16V8M8 12l4-4 4 4',
  'arrow.down.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 8v8M8 12l4 4 4-4',
  'arrow.down.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 8v8M8 12l4 4 4-4',
  'arrow.right.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12h8M12 8l4 4-4 4',
  'arrow.right.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12h8M12 8l4 4-4 4',
  'arrow.left.circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM16 12H8M12 8l-4 4 4 4',
  'arrow.left.circle.fill': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM16 12H8M12 8l-4 4 4 4',
  'arrow.up': 'M12 19V5M5 12l7-7 7 7',
  'arrow.down': 'M12 5v14M5 12l7 7 7-7',
  'doc.fill': 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z',
};

export function Icon({ name, size = 20, color = 'currentColor', className = '', weight = 'regular', testId }: IconProps) {
  const d = ICONS[name];
  if (!d) {
    return <span className={className} style={{ fontSize: size, lineHeight: 1 }} data-testid={testId}>●</span>;
  }
  const stroke = weight === 'bold' ? 2.4 : 1.7;
  const fill = name.includes('.fill') ? color : 'none';
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={['inline-block shrink-0', className].join(' ')}
      fill={fill}
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      data-testid={testId}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
